import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { TeamItem } from './TeamItem/TeamItem';

import { AccountMenu } from '@wetransfer/transfer/component/AccountMenu/AccountMenu';
import { AccountMenuItem } from '@wetransfer/transfer/component/AccountMenu/AccountMenuItem/AccountMenuItem';
import { AccountMenuItemGroup } from '@wetransfer/transfer/component/AccountMenu/AccountMenuItemGroup/AccountMenuItemGroup';
import { AccountMenuLink } from '@wetransfer/transfer/component/AccountMenu/AccountMenuLink/AccountMenuLink';
import { AccountMenuList } from '@wetransfer/transfer/component/AccountMenu/AccountMenuList/AccountMenuList';
import {
  getThumbnailURL,
  getAccountMenuName,
} from '@wetransfer/transfer/component/navigation/helpers/link';
import Links from '@wetransfer/transfer/component/navigation/links';
import { TransferAccountMenuButton } from '@wetransfer/transfer/component/TransferAccountMenu/TransferAccountMenuButton/TransferAccountMenuButton';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import { FF_ENSEMBLE } from '@wetransfer/transfer/constant/experiments';
import UnlimitedStorageExperiment from '@wetransfer/transfer/constant/experiments/unlimited-storage-experiment';
import ExperimentWorkspaceTrackingImplementation from '@wetransfer/transfer/constant/experiments/workspace-tracking-implementation';
import Helper from '@wetransfer/transfer/helper';
import {
  userHasPaidTier,
  userHasFreeTier,
  userHasPro2022Tier,
  userHasPlusTier,
  userHasPremium2022Tier,
  getPlanTier,
  userCanHaveSeats,
} from '@wetransfer/transfer/helper/user';
import { useCreateTeamForOwnAccount } from '@wetransfer/transfer/hooks/useCreateTeamForOwnAccount/useCreateTeamForOwnAccount';
import { useCurrentTeam } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import { useCurrentUser } from '@wetransfer/transfer/hooks/useCurrentUser/useCurrentUser';
import Analytics from '@wetransfer/transfer/modules/analytics';
import I18n from '@wetransfer/transfer/modules/i18n';

import './TransferAccountMenu.scss';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import { User } from '@wetransfer/transfer/spaces-bundle/store/types';
import Notification from '@wetransfer/transfer/store/action/notification';
import { State } from '@wetransfer/transfer/store/types';

import { CreateTeamAccountItem } from './CreateTeamAccountItem/CreateTeamAccountItem';

export interface Props {
  user: User;
  isLogOutPending: boolean;
  logout(): void;
  subdomainName?: string;
  showLabel: boolean;
  auth0UserId: string;
}

export const TransferAccountMenu: React.FC<Props> = ({
  user,
  isLogOutPending,
  logout,
  subdomainName,
  showLabel = true,
  auth0UserId,
}) => {
  const dispatch = useDispatch();
  const [menuLabel, setMenuLabel] = useState('');

  const searchParams = new URLSearchParams(location.search);

  const { auth, route, domain, currency, language } = useSelector(
    (state: State) => ({
      auth: state.auth,
      route: state.route,
      domain: state.session.domain,
      currency: state.session.region.currency,
      language: state.session.language,
      account: state.account,
    })
  );
  const currencyCode = currency || 'USD';

  const {
    currentTeam,
    allTeams,
    fetching: isFetchingCurrentTeam,
    onTeamSwitch,
    isCurrentTeamOfMany,
  } = useCurrentTeam();
  const { currentUser, userHasTeamOfMany } = useCurrentUser();
  const stripeCustomerId = currentTeam?.stripeCustomerId;

  // For mobile subscribers that are accessing the web. They should not see the `Manage Workspace Button`.
  // they don't have a currency set in the backend and it causes myaccount to not display
  const userCanAddSeats = useSelector((state: State) =>
    userCanHaveSeats(state.account)
  );

  const { fetching: isCreatingTeam, createTeamForOwnAccount } =
    useCreateTeamForOwnAccount();

  useEffect(() => {
    if (!isFetchingCurrentTeam) {
      setMenuLabel(
        getAccountMenuName({
          isCurrentTeamOfMany,
          currentTeam,
          user,
          subdomainName,
        })
      );
    }
  }, [
    isCurrentTeamOfMany,
    currentTeam,
    isFetchingCurrentTeam,
    user,
    subdomainName,
  ]);

  const shouldShowTeamNotification = searchParams.get('showTeamNotification');

  useEffect(() => {
    if (currentTeam && shouldShowTeamNotification) {
      showTeamNotification();
    }
  }, [currentTeam, user, shouldShowTeamNotification]);

  // check team name to know if user just accept the invite
  const teamName: string = searchParams.get('teamName');

  useEffect(() => {
    if (teamName && currentUser && currentTeam && userHasTeamOfMany) {
      // we need to have a timeout here to wait until the page is fully loaded
      // then switch the team
      setTimeout(() => {
        const teamToSwitch = currentUser?.teams.find(
          (t) => t.name === teamName
        );

        teamToSwitch && switchTeam(teamToSwitch.id);
      }, 500);
    }
  }, [teamName, currentUser, currentTeam, userHasTeamOfMany]);

  useEffect(() => {
    if (stripeCustomerId) window.profitwell('user_id', stripeCustomerId);
  }, [stripeCustomerId]);

  const avatarURL = getThumbnailURL(user);
  const teamLogoURL = currentTeam?.logoUrl;

  function showTeamNotification() {
    const getPlan = (user: User): string => {
      // Covers Pro users who are on a Premium trial
      if (userHasPlusTier(user) || userHasPremium2022Tier(user)) {
        return 'WeTransfer Premium';
      }

      // Covers Pro users until the flag is enabled
      if (userHasPro2022Tier(user) || userHasPlusTier(user)) {
        return 'WeTransfer Pro';
      }

      return 'WeTransfer';
    };

    dispatch(
      Notification.setNotification(
        'notification',
        'current_team_notification',
        {
          plan: getPlan(user),
          currentTeam: Helper.sanitizeHtml(currentTeam.name),
        }
      )
    );
  }

  // Check whether the user is only a member of a team
  // and does not have their own free or Pro account
  const userHasTeamOfOne =
    !currentTeam?.id ||
    Boolean(currentUser?.teams.find((team) => team.creatorId === auth0UserId));

  function switchTeam(id: string) {
    if (id === currentTeam?.id) return;

    if (ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()) {
      trackAmplitudeEvent({
        action: 'Workspace Switched',
      });
    }

    onTeamSwitch(id);

    const teamToSwitch = currentUser?.teams.find((team) => team.id === id);
    const channelName = teamToSwitch?.channel?.name;

    const url = new URL(
      `${location.protocol}//${
        channelName ? `${channelName}.` : ''
      }${domain}/transfers`
    );

    if (location.port !== '') {
      url.port = location.port;
    }

    url.searchParams.set('showTeamNotification', 'true');

    location.assign(url.href);
  }

  async function createTeam() {
    if (isCreatingTeam) return;

    const { data } = await createTeamForOwnAccount();
    switchTeam(data.createTeamForOwnAccount.team.id);
  }

  const amplitudeTracking = useCallback(() => {
    trackAmplitudeEvent({
      action: 'Manage Workspace Clicked',
      additionalProperties: {
        Context: 'Account Menu',
      },
    });
  }, []);

  function portalsClickTrack() {
    Analytics.trackSnowplowEvent({
      category: 'navigation_bar',
      action: 'clickNavPortals',
      label: getPlanTier(user) || 'anonymous',
    });
  }

  function trackAccountLinkClick(trackEvent: Record<string, string>) {
    Analytics.trackSnowplowEvent({
      category: trackEvent.category,
      action: trackEvent.action,
      label: getPlanTier(user) || 'anonymous',
    });
  }

  function getAvatar() {
    if (currentTeam || userHasTeamOfMany) {
      return teamLogoURL;
    }

    // individual pro users OR free account users
    if ((userHasTeamOfOne || !currentTeam) && !auth?.isLoading) {
      return avatarURL;
    }

    return '';
  }

  function getInviteTeamUrl() {
    const currentVariant = UnlimitedStorageExperiment.currentVariant();

    const currency = `&currency=${currencyCode}`;
    const ff = '&FF_pricing=true';
    const storageFF = currentVariant ? `&storageFF=${currentVariant}` : '';

    return `${Links.teamsItem.path}${ff}${currency}${storageFF}`;
  }

  return (
    <AccountMenu>
      <TransferAccountMenuButton
        showLabel={showLabel}
        avatarURL={getAvatar()}
        menuLabel={menuLabel}
        language={language}
      />
      <AccountMenuList className="TransferAccountMenu__list">
        <p className="AccountMenuList__email" title={user.email}>
          {user.email}
        </p>
        <React.Fragment>
          {userHasTeamOfMany && (
            <AccountMenuItemGroup
              showSeparator
              scroll
              className="AccountMenuItemGroup__switcher"
            >
              {currentUser?.teams.map((team) => {
                return (
                  <AccountMenuItem
                    onSelect={() => {
                      switchTeam(team.id);
                    }}
                    key={team.id}
                  >
                    <TeamItem
                      team={allTeams?.find(
                        (userTeam) => userTeam.id === team.id
                      )}
                      isCurrentTeam={currentTeam?.id === team.id}
                    />
                  </AccountMenuItem>
                );
              })}
              {!userHasTeamOfOne && (
                <AccountMenuItem
                  onSelect={createTeam}
                  disabled={!user.isVerified}
                >
                  <CreateTeamAccountItem
                    userName={currentUser?.givenName}
                    isCreatingTeam={isCreatingTeam}
                  />
                </AccountMenuItem>
              )}
            </AccountMenuItemGroup>
          )}
          <AccountMenuItemGroup showSeparator={true}>
            <AccountMenuLink
              href={Links.accountItem.path}
              active={Browser.isMobile && route.path === Links.accountItem.path}
              onSelect={() =>
                trackAccountLinkClick(Links.accountItem.trackEvent)
              }
            >
              {I18n.t(Links.accountItem.name)}
            </AccountMenuLink>
            {userCanAddSeats && (
              <AccountMenuLink
                href={getInviteTeamUrl()}
                onSelect={amplitudeTracking}
                data-testid="team-link"
              >
                {I18n.t('page.workspace_bar.manage_workspace')}
              </AccountMenuLink>
            )}
          </AccountMenuItemGroup>
          {Browser.isMobile ? (
            <AccountMenuItemGroup>
              {getVariant(FF_ENSEMBLE, 'on') === 'on' && userHasPaidTier(user) && (
                <AccountMenuLink
                  href={Links.portalsItem.url}
                  onSelect={portalsClickTrack}
                >
                  {I18n.t(Links.portalsItem.name)}
                </AccountMenuLink>
              )}
              <AccountMenuLink
                href={Links.transfersItem.path}
                active={route.path === Links.transfersItem.path}
                onSelect={() =>
                  trackAccountLinkClick(Links.transfersItem.trackEvent)
                }
              >
                {I18n.t(Links.transfersItem.name)}
              </AccountMenuLink>
              <AccountMenuLink
                href={Links.contactsItem.path}
                active={route.path === Links.contactsItem.path}
                onSelect={() =>
                  trackAccountLinkClick(Links.contactsItem.trackEvent)
                }
              >
                {I18n.t(Links.contactsItem.name)}
              </AccountMenuLink>
              <AccountMenuLink
                href={Links.brandingItem.path}
                active={route.path === Links.brandingItem.path}
                onSelect={() =>
                  trackAccountLinkClick(Links.brandingItem.trackEvent)
                }
              >
                {I18n.t(Links.brandingItem.name)}
              </AccountMenuLink>
              {userHasFreeTier(user) && (
                <AccountMenuLink
                  href={Links.upgradeItem.path}
                  onSelect={() =>
                    trackAccountLinkClick(Links.upgradeItem.trackEvent)
                  }
                >
                  {I18n.t(Links.upgradeItem.name)}
                </AccountMenuLink>
              )}
            </AccountMenuItemGroup>
          ) : (
            <AccountMenuItemGroup>
              <AccountMenuLink
                href={Links.helpItem.path}
                onSelect={() =>
                  trackAccountLinkClick(Links.helpItem.accountTrackEvent)
                }
              >
                {I18n.t(Links.helpItem.name)}
              </AccountMenuLink>
              <AccountMenuLink
                href={Links.aboutItem.url}
                external={true}
                onSelect={() =>
                  trackAccountLinkClick(Links.aboutItem.accountTrackEvent)
                }
              >
                {I18n.t(Links.aboutItem.name)}
              </AccountMenuLink>
            </AccountMenuItemGroup>
          )}
          <AccountMenuItem disabled={isLogOutPending} onSelect={() => logout()}>
            {I18n.t(Links.signoutItem.name)}
          </AccountMenuItem>
        </React.Fragment>
      </AccountMenuList>
    </AccountMenu>
  );
};
